import React from 'react';
import { errorMessage } from '../../api';
import type { AsyncData } from '../../hooks/useAsyncData';
import type { ProjectReadiness } from '../../types';
import { Button, Card, CardBody, InlineAlert, Skeleton } from '../../ui';

/**
 * Pre-launch checklist (GET /project/{id}/readiness). `problems` stop the launch and are listed so they can be fixed;
 * `warnings` are advice and never block. The Launch button follows the same `ready` flag.
 */
export function LaunchChecklist({ readiness }: { readiness: AsyncData<ProjectReadiness | null> }) {
  const r = readiness.data;

  let body: React.ReactNode;
  if (readiness.error) {
    body = (
      <InlineAlert tone="warn">
        <span>Could not check whether this campaign is ready: {errorMessage(readiness.error)} </span>
        <button type="button" onClick={readiness.reload} className="font-semibold underline">Try again</button>
      </InlineAlert>
    );
  } else if (!r) {
    body = readiness.loading
      ? <div aria-busy="true"><Skeleton className="h-10 w-full" /></div>
      : <InlineAlert tone="info">The launch check is not available for this campaign.</InlineAlert>;
  } else {
    body = (
      <>
        {r.ready
          ? <InlineAlert tone="info">Everything needed to launch is in place.</InlineAlert>
          : (
            <div>
              <p className="text-sm font-semibold text-ink">Before you can launch:</p>
              <ul aria-label="Problems to fix" className="mt-2 space-y-1.5 text-sm text-ink">
                {r.problems.map((p) => <li key={p} className="flex gap-2"><span aria-hidden="true" className="text-danger">✗</span><span>{p}</span></li>)}
              </ul>
            </div>
          )}
        {r.warnings.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-semibold text-ink">Worth a look (does not block launching):</p>
            <ul aria-label="Warnings" className="mt-2 space-y-1.5 text-sm text-muted">
              {r.warnings.map((w) => <li key={w} className="flex gap-2"><span aria-hidden="true" className="text-warn">!</span><span>{w}</span></li>)}
            </ul>
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-ink">Ready to launch?</h2>
          <Button variant="ghost" size="sm" onClick={readiness.reload} loading={readiness.loading && Boolean(r)}>Check again</Button>
        </div>
        {body}
      </CardBody>
    </Card>
  );
}
